import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'

import { myBranchesQueryOptions } from '@/api/reference-api'
import { session } from '@/api/session'
import type { Branch } from '@/api/types'
import { useAuth } from '@/auth/use-auth'

/**
 * The branch the operator is working in, and the act of changing it.
 *
 * It lives here rather than inside a component because the wide layout offers it as a select in
 * the top bar and the narrow layout offers it inside a compact menu, and the two must not be able
 * to drift apart on what a switch does.
 *
 * Changing branch changes what every subsequent request is scoped to, so the cache is emptied on
 * the switch: a customer list fetched for Kaneshie must never be shown under Madina's header.
 * The selection itself grants nothing — it rides along as a header and the server checks it
 * against the user's branch access on every request.
 */
const EMPTY_BRANCHES: Branch[] = []

export interface BranchSelection {
  branches: Branch[]
  isPending: boolean
  selectedBranchId: string | null
  currentBranch: Branch | undefined
  /** False when the user holds a single branch: there is nothing to choose. */
  canSwitch: boolean
  selectBranch: (branchId: string) => void
}

export function useBranchSelection(): BranchSelection {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { data, isPending, isError } = useQuery(myBranchesQueryOptions())

  // Defended twice on purpose. The query already narrows the payload, and this tolerates an
  // unexpected shape reaching here anyway, because the header renders above every screen: a
  // reference lookup that answers oddly should cost the branch selector, not the application.
  //
  // Memoised so the identity is stable across renders and the effect below can depend on it
  // honestly rather than on the payload it came from.
  const branches = useMemo(
    () => (Array.isArray(data?.items) ? data.items : EMPTY_BRANCHES),
    [data],
  )
  const selectedBranchId = session.getBranchId() ?? user?.homeBranchId ?? null

  // A stored branch from a previous session may no longer be permitted — an access review can
  // revoke it overnight. Fall back to the home branch rather than sending a header the server
  // will reject on every request.
  useEffect(() => {
    const stored = session.getBranchId()

    // A non-empty list can only exist once the payload arrived with one, so this needs no
    // separate check that the query has settled.
    if (branches.length > 0) {
      if (!stored || !branches.some((branch) => branch.id === stored)) {
        session.setBranchId(user?.homeBranchId ?? branches[0].id)
      }
      return
    }

    /*
     * The list could not be read at all, and there is a stored branch that therefore cannot be
     * confirmed. Keeping it is the worse of the two failures: it rides on every subsequent request
     * as `X-Branch-Id`, and a stale id — left behind by an earlier build, or by access that has
     * since been revoked — scopes every read and write to a branch nobody chose, silently. The
     * home branch is the one branch the server itself named when it issued this session, so it is
     * the only value this client can fall back to and still be sure of.
     *
     * Only on a settled failure, never while the query is still in flight: correcting a selection
     * against a list that has not arrived yet would reset it on every page load.
     */
    if (isError && user?.homeBranchId && stored !== user.homeBranchId) {
      session.setBranchId(user.homeBranchId)
    }
  }, [branches, isError, user?.homeBranchId])

  return {
    branches,
    isPending,
    selectedBranchId,
    currentBranch: branches.find((branch) => branch.id === selectedBranchId),
    canSwitch: branches.length > 1,
    selectBranch: (branchId: string) => {
      session.setBranchId(branchId)
      queryClient.clear()
    },
  }
}
